import { SITE_ENV_LABELS, SiteWithStacks } from '@pubstack/common/src/site'
import { parseAsInteger, parseAsNumberLiteral, parseAsString, useQueryState } from 'nuqs'
import { FunctionComponent, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSitesWithPagination, useUpdateSite } from '~/api/app/sites.api'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { ActionButtons, ActionsTableCell, NoResultsTableCell } from '~/components/_styled/table'
import Button from '~/components/Button'
import { IllustrationMessage } from '~/components/IllustrationMessage'
import { BaseInputElement } from '~/components/Input'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { PageContent } from '~/components/PageContent'
import { PAGE_LIMIT_OPTIONS, Pagination } from '~/components/Pagination'
import Table, { TableColumns } from '~/components/table/Table'
import TableCell from '~/components/table/TableCell'
import TableRow from '~/components/table/TableRow'
import { Tooltip } from '~/components/Tooltip'
import { useBreadcrumbs } from '~/hooks/useBreadcrumbs'
import { useDebouncedCallback } from '@mantine/hooks'
import { UnarchiveConfirmationModal } from '~/feature-components/UnarchiveConfirmationModal'
import { siteProductOf } from './siteProduct'
import { SiteProductIndicator } from './SiteProductIndicator'

const ITEMS_PER_PAGE = 25
const SEARCH_DEBOUNCE_TIME = 300

const COLUMNS: TableColumns<SiteWithStacks> = [
  { name: 'Media', isSortable: false },
  { name: 'Product', isSortable: false },
  { name: 'Environment', isSortable: false },
  { name: 'Actions', isSortable: false },
]

/**
 * One archived media, and the way back.
 *
 * The row is rendered `archived`, which is what the table already uses to grey a row out and stop it
 * being clickable — an archived media has no page to open, since every product tree filters it out.
 */
const ArchivedMediaTr: FunctionComponent<{ media: SiteWithStacks }> = ({ media }) => {
  const user = useAuthenticatedUser()
  const modal = useGlobalModal()
  const { mutateAsync: updateSite, isPending } = useUpdateSite(user.currentScopeId, media.id, {
    successToast: (
      <>
        <strong>{media.name}</strong> was restored.
      </>
    ),
    errorToast: (
      <>
        An error occurred while trying to restore <strong>{media.name}</strong>.
      </>
    ),
  })

  const product = siteProductOf(media)
  const servesAds = product !== 'analytics'

  const handleUnarchive = async () => {
    if (isPending) {
      return
    }
    // Without its stacks: restoring them is the server's job, driven by the `site:unarchived` event.
    const { stacks: _stacks, ...site } = media
    await updateSite({ ...site, enabled: true })
  }

  return (
    <TableRow archived>
      <TableCell>{media.name}</TableCell>
      <TableCell>
        <SiteProductIndicator product={product} />
      </TableCell>
      <TableCell>{media.env ? SITE_ENV_LABELS[media.env] : 'Web'}</TableCell>
      <ActionsTableCell>
        <ActionButtons>
          <Tooltip title={'Unarchive'}>
            <Button
              iconName={isPending ? 'pubstack' : 'unarchive'}
              iconSpin={isPending}
              disabled={isPending}
              variant={'tertiary'}
              title={'Unarchive'}
              onClick={() =>
                modal.open(UnarchiveConfirmationModal, {
                  name: media.name,
                  noun: 'media',
                  consequence: servesAds
                    ? `It will return to the media list, its stacks will be restored and start serving again, and its bid params mappings will be put back. Its data will be visible in Pubstack again.`
                    : 'It will return to the media list, and its data will be visible in Pubstack again.',
                  onUnarchive: handleUnarchive,
                  isPending,
                  trackingPrefix: 'common-archived-medias-unarchive',
                })
              }
              trackingLabel={'common-archived-medias-unarchive'}
            />
          </Tooltip>
        </ActionButtons>
      </ActionsTableCell>
    </TableRow>
  )
}

/**
 * The archived medias, on their own page rather than as a filter on the media list.
 *
 * Archiving is meant to make a media go away, so the list a publisher works in every day shows only the
 * live ones and never offers an "Archived" option that could be left on by accident. This page is where
 * the archived ones are, and reaching it is owner-only — enforced by the route's `accessRole` and again
 * by the API, which refuses `archived=true` to anyone below owner.
 *
 * Restoring is the owner's too: unarchiving replays the archive in reverse, bringing stacks back into
 * serving and restoring bid params mappings, so it asks for the media name to be typed back first.
 */
export const ArchivedMediasPage: FunctionComponent = () => {
  const user = useAuthenticatedUser()
  const navigate = useNavigate()
  const breadcrumbs = useBreadcrumbs()
  const scopeId = user.currentScopeId

  const [page, setPage] = useQueryState<number>('page', parseAsInteger.withDefault(1).withOptions({ clearOnDefault: false }))
  const [limit, setLimit] = useQueryState<number>('limit', parseAsNumberLiteral(PAGE_LIMIT_OPTIONS).withDefault(ITEMS_PER_PAGE).withOptions({ clearOnDefault: false }))
  const [search, setSearch] = useQueryState<string>('search', parseAsString.withDefault(''))
  const [localSearch, setLocalSearch] = useState(search)
  // Resync from the URL-backed `search` ("previous value" pattern — adjust during render).
  const [prevSearch, setPrevSearch] = useState(search)
  if (search !== prevSearch) {
    setPrevSearch(search)
    setLocalSearch(search)
  }

  const debouncedSearch = useDebouncedCallback((value: string) => {
    setSearch(value)
    setPage(1)
  }, SEARCH_DEBOUNCE_TIME)

  const {
    data: { data: medias, pagination },
    isLoading,
  } = useSitesWithPagination({
    scopeId,
    filters: { search, archived: true },
    pagination: { page, limit },
    sortBy: 'name',
    sortOrder: 'ascending',
  })

  return (
    <PageContent breadcrumbs={breadcrumbs}>
      <div className={'flex flex-col gap-4'}>
        {/* Back sits where the media list keeps its overflow menu — top right, above the table. The two
            pages are the same list twice, so the control that swaps between them stays in one place. */}
        <div className={'flex flex-wrap items-center gap-4'}>
          <BaseInputElement
            type={'text'}
            iconLeft={'search'}
            labelIsPlaceholder
            label={'Search a media'}
            value={localSearch}
            onChange={(event) => {
              setLocalSearch(event.target.value)
              debouncedSearch(event.target.value)
            }}
          />
          <div className={'flex-1'} />
          <Button variant={'tertiary'} iconName={'chevron_left'} onClick={() => navigate('/monetization/inventory/medias')} trackingLabel={'common-archived-medias-back-navigate'}>
            Back to medias
          </Button>
        </div>

        {isLoading || medias.length > 0 ? (
          <>
            <Table columns={COLUMNS} isLoading={isLoading}>
              {medias.length > 0 ? (
                medias.map((media) => <ArchivedMediaTr key={media.id} media={media} />)
              ) : (
                <TableRow>
                  <NoResultsTableCell colspan={COLUMNS.length}>No results</NoResultsTableCell>
                </TableRow>
              )}
            </Table>

            <Pagination
              currentPage={page}
              totalPages={pagination.totalPages}
              limit={limit}
              totalItems={pagination.total}
              onPageChange={(next) => setPage(next)}
              onLimitChange={(next) => {
                setLimit(next)
                setPage(1)
              }}
            />
          </>
        ) : (
          <IllustrationMessage iconName={'night_sky'} iconSize={'190px'} title={''} message={'No archived media.'} />
        )}
      </div>
    </PageContent>
  )
}
